import React, { useState } from 'react'
import { 
  FiShield, 
  FiKey, 
  FiCheckCircle, 
  FiAlertTriangle, 
  FiRefreshCw,
  FiLock,
  FiChevronRight
} from 'react-icons/fi'

export const SecuritySection = ({ 
  onPasswordChange,
  on2FASetup,
  on2FADisable,
  is2FAEnabled,
  is2FALoading
}) => {
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [saving, setSaving] = useState(false)

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas')
      return
    }
    
    setPasswordError('')
    setSaving(true)
    
    try {
      const success = await onPasswordChange({
        currentPassword,
        newPassword,
        confirmPassword
      })
      
      if (success) {
        setPasswordSuccess('Votre mot de passe a été mis à jour avec succès')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setShowChangePassword(false)
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setPasswordSuccess('')
        }, 5000)
      }
    } catch (error) {
      setPasswordError(error.message || 'Une erreur est survenue lors de la mise à jour du mot de passe')
    } finally {
      setSaving(false)
    }
  }

  const handle2FAToggle = async () => {
    if (is2FAEnabled) {
      const confirmDisable = window.confirm(
        'Êtes-vous sûr de vouloir désactiver l\'authentification à deux facteurs ? Votre compte sera moins sécurisé.'
      )
      
      if (confirmDisable) {
        try {
          await on2FADisable()
        } catch (error) {
          console.error('Error disabling 2FA:', error)
        }
      }
    } else {
      try {
        await on2FASetup()
      } catch (error) {
        console.error('Error setting up 2FA:', error)
      }
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Sécurité du compte</h2>
        <p className="mt-1 text-sm text-gray-400">
          Gérez la sécurité de votre compte et vos paramètres d'authentification
        </p>
      </div>

      <div className="bg-gray-800/50 border border-amber-500/20 rounded-2xl shadow-lg overflow-hidden backdrop-blur-sm">
        <div className="px-6 py-5 border-b border-amber-500/20">
          <h3 className="text-lg font-semibold text-white">
            Authentification et sécurité
          </h3>
          <p className="mt-1 text-sm text-gray-400">
            Paramètres de sécurité et d'authentification de votre compte
          </p>
        </div>
        
        <div className="divide-y divide-amber-500/10">
          {/* Mot de passe */}
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-base font-medium text-white">Mot de passe</h4>
                <p className="text-sm text-gray-400">Mettez à jour votre mot de passe régulièrement</p>
              </div>
              <button
                type="button"
                onClick={() => setShowChangePassword(!showChangePassword)}
                className="inline-flex items-center px-4 py-2 border border-amber-500/30 text-sm font-medium rounded-lg text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50 transition-all duration-300"
              >
                Changer le mot de passe
              </button>
            </div>

            {showChangePassword && (
              <div className="mt-6 p-6 bg-gray-700/30 border border-amber-500/20 rounded-xl">
                {passwordError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <FiAlertTriangle className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-300">
                          Erreur
                        </h3>
                        <div className="mt-1 text-sm text-red-200">
                          {passwordError}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {passwordSuccess && (
                  <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <FiCheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-300">
                          Succès
                        </h3>
                        <div className="mt-1 text-sm text-green-200">
                          {passwordSuccess}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-gray-400 mb-1">
                      Mot de passe actuel
                    </label>
                    <div className="mt-1">
                      <input
                        id="current-password"
                        name="current-password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="block w-full rounded-lg border border-amber-500/30 bg-gray-700/50 text-white focus:border-amber-500 focus:ring-amber-500/50 sm:text-sm p-2.5 transition-all duration-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-400 mb-1">
                      Nouveau mot de passe
                    </label>
                    <div className="mt-1">
                      <input
                        id="new-password"
                        name="new-password"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="block w-full rounded-lg border border-amber-500/30 bg-gray-700/50 text-white focus:border-amber-500 focus:ring-amber-500/50 sm:text-sm p-2.5 transition-all duration-300"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Le mot de passe doit contenir au moins 8 caractères
                    </p>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-400 mb-1">
                      Confirmer le nouveau mot de passe
                    </label>
                    <div className="mt-1">
                      <input
                        id="confirm-password"
                        name="confirm-password"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="block w-full rounded-lg border border-amber-500/30 bg-gray-700/50 text-white focus:border-amber-500 focus:ring-amber-500/50 sm:text-sm p-2.5 transition-all duration-300"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowChangePassword(false)
                        setPasswordError('')
                        setPasswordSuccess('')
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700/50 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/30 transition-all duration-300"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      {saving ? (
                        <>
                          <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          Enregistrement...
                        </>
                      ) : (
                        'Enregistrer les modifications'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* 2FA */}
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-base font-medium text-white">Authentification à deux facteurs (2FA)</h4>
                <p className="text-sm text-gray-400">
                  {is2FAEnabled 
                    ? 'L\'authentification à deux facteurs est activée sur votre compte.'
                    : 'Ajoutez une couche de sécurité supplémentaire à votre compte.'}
                </p>
              </div>
              <button
                type="button"
                onClick={handle2FAToggle}
                disabled={is2FALoading}
                className={`inline-flex items-center px-4 py-2 border ${
                  is2FAEnabled
                    ? 'border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20'
                    : 'border-amber-500/30 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20'
                } text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300`}
              >
                {is2FALoading ? (
                  <FiRefreshCw className="animate-spin h-4 w-4 mr-2" />
                ) : is2FAEnabled ? (
                  'Désactiver la 2FA'
                ) : (
                  'Activer la 2FA'
                )}
              </button>
            </div>
          </div>

          {/* Sessions actives */}
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-base font-medium text-white">Sessions actives</h4>
                <p className="text-sm text-gray-400">Gérez vos sessions actives sur d'autres appareils</p>
              </div>
              <button
                type="button"
                className="inline-flex items-center text-sm font-medium text-amber-400 hover:text-amber-300 focus:outline-none transition-colors duration-300"
              >
                Voir toutes les sessions
                <FiChevronRight className="ml-1 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SecuritySection
